import React from 'react';
import { observer } from 'mobx-react-lite';
import { Box, Chip, Grow, IconButton, Paper, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { ChatMessageList } from './ChatMessageList';
import { ChatInput } from './ChatInput';
import { QuickReplyButtons } from './QuickReplyButtons';
import { ResolvePrompt } from './ResolvePrompt';
import { useScreens } from '@shared/hooks/useScreens';
import {
  type ChatMessageType,
  type ChatStatus,
  type MessageSender,
  type QuickReply,
} from '../model/chatTypes';

interface ChatWindowProps {
  messages: ChatMessageType[];
  isTyping: boolean;
  typingSender: MessageSender;
  quickReplies: QuickReply[];
  buttonsQuestion: string | null;
  status: ChatStatus;
  onSendMessage: (text: string) => void;
  onQuickReply: (text: string) => void;
  onClose: () => void;
  onNewChat?: () => void;
  showResolvePrompt: boolean;
  onResolveYes: () => void;
  onResolveNo: () => void;
}

const statusLabels: Record<ChatStatus, string> = {
  disconnected: 'Отключен',
  connecting: 'Подключение...',
  bot: 'Бот',
  waiting_operator: 'Ожидание оператора',
  with_operator: 'Оператор',
  resolved: 'Завершён',
};

const statusColors: Record<ChatStatus, 'default' | 'primary' | 'success' | 'warning' | 'error' | 'info'> = {
  disconnected: 'default',
  connecting: 'info',
  bot: 'primary',
  waiting_operator: 'warning',
  with_operator: 'success',
  resolved: 'default',
};

export const ChatWindow: React.FC<ChatWindowProps> = observer(({
  messages,
  isTyping,
  typingSender,
  quickReplies,
  buttonsQuestion,
  status,
  onSendMessage,
  onQuickReply,
  onClose,
  onNewChat,
  showResolvePrompt,
  onResolveYes,
  onResolveNo,
}) => {
  const { isMobile } = useScreens();

  return (
    <Grow in>
      <Paper
        elevation={8}
        sx={{
          position: 'fixed',
          zIndex: 1400,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          borderRadius: isMobile ? 0 : 3,
          ...(isMobile
            ? {
                bottom: 0,
                right: 0,
                left: 0,
                top: 0,
                width: '100vw',
                height: '100vh',
              }
            : {
                bottom: 90,
                right: 40,
                width: 380,
                maxHeight: 520,
                height: 520,
              }),
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 2,
            py: 1.5,
            bgcolor: 'primary.main',
            color: 'primary.contrastText',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="subtitle1" fontWeight={600}>
              Поддержка eOSI
            </Typography>
            <Chip
              label={statusLabels[status]}
              color={statusColors[status]}
              size="small"
              sx={{ height: 20, fontSize: '0.65rem' }}
            />
          </Box>
          <IconButton onClick={onClose} size="small" sx={{ color: 'primary.contrastText', flexShrink: 0 }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        {/* Messages */}
        <ChatMessageList messages={messages} isTyping={isTyping} typingSender={typingSender} />

        {/* Quick replies */}
        <QuickReplyButtons
          quickReplies={quickReplies}
          buttonsQuestion={buttonsQuestion}
          onQuickReply={onQuickReply}
        />

        {/* Prompt «Вопрос решён?» — только в диалоге с оператором */}
        {showResolvePrompt && status === 'with_operator' && (
          <ResolvePrompt onYes={onResolveYes} onNo={onResolveNo} />
        )}

        {/* Input */}
        <ChatInput status={status} isTyping={isTyping} onSendMessage={onSendMessage} onNewChat={onNewChat} />
      </Paper>
    </Grow>
  );
});
